<template>
	<div class="absolute top-full inset-x-0 container" :style="cssVars">
		<div ref="bodyRef" class="bg-white shadow-sm rounded-b-2xl p-4">
			<Transition
				mode="out-in"
				enter-from-class="opacity-0"
				leave-to-class="opacity-0"
			>
				<div
					v-if="selectedHypermarket"
					class="typo-body-s transition-opacity duration-300 grid grid-cols-3 text-body-secondary"
				>
					<div
						ref="scrollBodyRef"
						class="grid col-span-2 overflow-y-auto min-h-96 max-h-[calc(100vh-var(--header-height)-64px)] pb-0.5 grid-cols-2"
					>
						<section class="space-y-4 pr-5">
							<BranchWidgetOpeningHours aria-describedby="branch-opening-hours-menu-widget" :hidden-detail-link="selectedBranchLabelFresh" @update:click="handleInfoClick">
								<template #header>
									<h2 id="branch-opening-hours-menu-widget" class="typo-h4 mb-4 text-heading-primary">
										Otevírací doba
									</h2>
								</template>
							</BranchWidgetOpeningHours>
							<BranchWidgetContact v-if="!selectedBranchLabelFresh" class="!mt-0" @update:click="handleInfoClick" />
							<BranchWidgetGasStation
								v-if="hypermarketDetails?.gasStation"
								aria-describedby="branch-gas-station-widget"
								@update:click="handleInfoClick"
							>
								<template #header>
									<h2 id="branch-gas-station-widget" class="typo-h4 mb-4 text-heading-primary">
										Čerpací stanice
									</h2>
								</template>
							</BranchWidgetGasStation>
						</section>

						<section class="space-y-4 px-5">
							<BranchWidgetContact v-if="selectedBranchLabelFresh" aria-describedby="branch-contact-widget" @update:click="handleInfoClick">
								<template #header>
									<h2 id="branch-contact-widget" class="typo-h4 mb-4 text-heading-primary">
										Adresa a kontakt
									</h2>
								</template>
							</BranchWidgetContact>
							<FoodMenuTodayWidget v-if="branchPageAvailability.hasRestaurant" class="space-y-2 lg:space-y-4 typo-body-s">
								<template #header>
									<h2 class="typo-h4 mb-4 text-heading-primary">
										Denní nabídka restaurace
									</h2>
									<div class="font-semibold capitalize mb-2">
										{{ format(now, 'eeee d. M. yyyy') }}
									</div>
								</template>
								<template #footer>
									<div class="flex gap-2 items-start !mt-4">
										<AbsoluteInternalLink
											:to="branchSpecificPath({name: 'sluzby-restaurace-nabidka'})"
											class="btn btn-primary btn-xs justify-center text-center max-xs:grow shadow-sm"
											@click="handleInfoClick('Úplná nabídka | Denní nabídka restaurace')"
										>
											Úplná nabídka
										</AbsoluteInternalLink>
										<AbsoluteInternalLink
											v-if="branchPageAvailability.hasOnlineOrder"
											:to="branchSpecificPath({name: 'online-objednavka-slug', params: {slug: 'restaurace'}})"
											class="btn btn-secondary btn-xs justify-center text-center max-xs:grow shadow-sm"
											@click="handleInfoClick('Objednat online | Denní nabídka restaurace')"
										>
											<NuxtIcon
												name="custom/arrow-right"
												class="text-lg transition duration-300"
												filled
												aria-hidden="true"
											/>
											Objednat online
										</AbsoluteInternalLink>
									</div>
								</template>
							</FoodMenuTodayWidget>

							<div
								v-if="hypermarketDetails?.carWash?.open && hypermarketDetails.carWash.pricelist?.length"
								class="space-y-2 lg:space-y-4 mt-4"
							>
								<CarWashPricelistTable
									:pricelist="hypermarketDetails.carWash.pricelist[0]"
									aria-describedby="car-wash-pricelist-table-caption"
									class="w-full pb-0.5"
									:pending="hypermarketDetailsLoading"
								>
									<template #header>
										<h2 id="car-wash-pricelist-table-caption" class="typo-h4 mb-4 text-heading-primary">
											Mycí linka
										</h2>
									</template>
									<template #footer>
										<AbsoluteInternalLink
											v-if="branchPageAvailability.hasServices"
											:to="branchSpecificPath({name: 'sluzby-slug', hash: '#ceny-mycich-programu', params: {slug: 'cerpaci-stanice-a-myci-linka'}})"
											class="btn btn-primary xl:btn-secondary btn-xs justify-center text-center max-xs:grow mt-4 !shadow-sm"
											:aria-label="`Zobrazit více o pobočce ${selectedHypermarket}`"
											@click="handleInfoClick('Více informací | Mycí linka')"
										>
											<NuxtIcon
												name="custom/arrow-right"
												class="text-lg transition duration-300 max-xl:hidden"
												filled
												aria-hidden="true"
											/>
											Více informací
										</AbsoluteInternalLink>
									</template>
								</CarWashPricelistTable>
							</div>
						</section>
					</div>

					<section class="flex-none pl-5 border-l border-border-secondary relative min-h-96">
						<div class="absolute inset-0">
							<HypermarketSelectionBanner
								class="px-4"
								show-links
								dropdown-default-hm
								list-with-search
								hide-category-filter
								@update:hm="closeMegamenu"
							>
								<template #header>
									<h3 class="typo-h4 mb-1.5 text-heading-primary">
										Přejít na stránky jiné pobočky
									</h3>
								</template>
								<template v-if="warning" #legend>
									<p>
										{{ warning }}
									</p>
								</template>
							</HypermarketSelectionBanner>
						</div>
					</section>
				</div>
				<div v-else>
					<HypermarketSelectionBanner
						class="!pt-0"
						show-links
						dropdown-default-hm
					>
						<template v-if="warning" #legend>
							<p>
								{{ warning }}
							</p>
						</template>
					</HypermarketSelectionBanner>
				</div>
			</Transition>
		</div>
	</div>
</template>

<script lang="ts" setup>
	import {useNow, useElementSize} from '@vueuse/core';
	import {format} from 'date-fns';
	import {storeToRefs} from 'pinia';
	import {useStoryblokHypermarketsStore} from '~/stores/storyblokHypermarkets';
	import {useShoppingCartStore} from '~/stores/shoppingCart';

	type Props = {
		targetButton?: HTMLElement | null
	};

	type Emits = {
		(e: 'update:click', payload: boolean): void
	};

	const props = defineProps<Props>();
	const emit = defineEmits<Emits>();

	const body = useTemplateRef('bodyRef');
	const branchSpecificPath = useBranchSpecificPath();

	onClickOutside(body, closeMegamenu, {ignore: [props.targetButton]});

	const headerElement = ref<HTMLElement | null>(null);

	onMounted(() => {
		if (import.meta.client) {
			headerElement.value = document.getElementById('header');
		}
	});

	const {height: headerHeight} = useElementSize(headerElement);
	const cssVars = computed(() => ({'--header-height': `${headerHeight.value}px`}));
	const now = useNow();

	const hypermarketsStore = useStoryblokHypermarketsStore();
	const {selectedHypermarket, selectedHypermarketInstance} = storeToRefs(hypermarketsStore);
	const selectedBranchLabelFresh = computed(() => selectedHypermarketInstance.value?.branchType === 'fresh');

	const shoppingCartStore = useShoppingCartStore();
	const {items} = storeToRefs(shoppingCartStore);
	const warning = computed(() => (
		items.value.length
			? 'Pokud změníte pobočku dojde k odstranění produktů z košíku online objednávky.'
			: undefined
	));

	const {data: hypermarketDetails, loading: hypermarketDetailsLoading} = useHypermarketDetails(selectedHypermarket);
	const {data: branchPageAvailability} = useBranchPageAvailability(selectedHypermarket);

	function closeMegamenu() {
		emit('update:click', false);
	}

	function handleInfoClick(text: string) {
		trackHeaderEvent('info_in_selector_top', text, {selected_hypermarket: selectedHypermarketInstance.value?.content.name});
		closeMegamenu();
	}
</script>
